import { useState, useRef } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { uploadImageToR2 } from '../api/client';
import { colors } from '../constants/colors';

interface ImageUploadProps {
  value: string | null;
  onChange: (url: string | null) => void;
  folder: 'listings' | 'shows' | 'avatars';
  label?: string;
}

export function ImageUpload({ value, onChange, folder, label = 'Image' }: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const publicUrl = await uploadImageToR2(file, folder);
      onChange(publicUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    onChange(null);
    setError(null);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div style={styles.container}>
      <label style={styles.label}>{label}</label>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        style={styles.hiddenInput}
      />

      {value ? (
        <div style={styles.previewContainer}>
          <img src={value} alt="Preview" style={styles.preview} />
          <div style={styles.previewOverlay}>
            <button
              type="button"
              onClick={handleClick}
              style={styles.changeButton}
              disabled={isUploading}
            >
              {isUploading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : 'Change'}
            </button>
            <button
              type="button"
              onClick={handleRemove}
              style={styles.removeButton}
              disabled={isUploading}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleClick}
          style={styles.uploadButton}
          disabled={isUploading}
        >
          {isUploading ? (
            <>
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <span>Uploading...</span>
            </>
          ) : (
            <>
              <Upload size={24} />
              <span>Click to upload</span>
              <span style={styles.uploadHint}>JPG, PNG, WebP, GIF (max 5MB)</span>
            </>
          )}
        </button>
      )}

      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
  },
  hiddenInput: {
    display: 'none',
  },
  uploadButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '32px',
    backgroundColor: colors.gray[100],
    border: `2px dashed ${colors.gray[300]}`,
    borderRadius: '12px',
    cursor: 'pointer',
    color: colors.gray[600],
    transition: 'all 0.15s',
  },
  uploadHint: {
    fontSize: '12px',
    color: colors.gray[400],
  },
  previewContainer: {
    position: 'relative',
    width: '100%',
    aspectRatio: '16/9',
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: colors.gray[100],
  },
  preview: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  previewOverlay: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    display: 'flex',
    gap: '8px',
  },
  changeButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 12px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  removeButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    backgroundColor: 'rgba(220, 38, 38, 0.9)',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  error: {
    fontSize: '13px',
    color: '#DC2626',
    padding: '8px 12px',
    backgroundColor: '#FEE2E2',
    borderRadius: '6px',
  },
};
